import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import { XIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Dialog, DialogClose, DialogContent, DialogTitle } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { ReactNode, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { APIUserSlice } from 'store/api/user';
import type { EmployeeListItemMinDto } from 'types/employees';
import { ListViewVisibility } from 'types/listViews';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { CONDITION_LABEL_IDS, FilterChip, filterChips, FilterState } from './filterChips';
import type { ListViewsModel, ViewDialog, ViewDialogValues } from './useListViews';

/** `shared` holds what the people picker selects; a stored view's people carry only its id and name. */
type FormValues = Pick<ViewDialogValues, 'name' | 'visibility'> & { shared: EmployeeListItemMinDto[] };

/** The list as it stands, for the "What the view keeps" box of a view saved from it. */
export type ListSnapshot = {
    filterState: FilterState;
    /** The sorted column's title and direction; none while the list keeps its own order. */
    sort?: { column: string; descending: boolean };
    /** How many columns show; none on a list without a column picker. */
    shownColumns?: number;
};

const initialValues = (dialog: ViewDialog): FormValues =>
    dialog.mode === 'create'
        ? { name: dialog.name, visibility: 'PRIVATE', shared: [] }
        : {
              name: dialog.view.name,
              visibility: dialog.view.visibility,
              shared: (dialog.view.sharedEmployees ?? []) as EmployeeListItemMinDto[],
          };

/** A filter as its chip reads, in a sentence: "Case status is In progress". */
const chipText = ({ label, condition, values, answer }: FilterChip) => {
    if (answer) return answer === 'no' ? t('filters.chip-no', { label }) : label;
    return [label, t(CONDITION_LABEL_IDS[condition]), values.join(', ')].filter(Boolean).join(' ');
};

const FieldLabel = ({ children }: { children: ReactNode }) => (
    <span className="text-foreground-secondary text-meta font-medium">{children}</span>
);

function KeepsBox({ snapshot }: { snapshot: ListSnapshot }) {
    const dateFormat = useEffectiveDateFormat();
    const filters = filterChips(snapshot.filterState, (value) => format(new Date(value), dateFormat)).map(chipText);
    const line = (labelId: string, text: ReactNode) => (
        <span>
            <strong className="font-semibold">
                <FormattedMessage id={labelId} />
            </strong>{' '}
            · {text}
        </span>
    );
    return (
        <div className="flex flex-col gap-1.5">
            <FieldLabel>
                <FormattedMessage id="views.keeps-title" />
            </FieldLabel>
            <div className="bg-surface-subtle text-foreground-secondary text-meta flex flex-col gap-1.5 rounded-md px-3.5 py-3">
                {line('views.keeps-filters', filters.length ? filters.join(' · ') : t('views.keeps-no-filters'))}
                {snapshot.sort
                    ? line(
                          'views.keeps-sort',
                          <FormattedMessage
                              id={snapshot.sort.descending ? 'views.keeps-sort-desc' : 'views.keeps-sort-asc'}
                              values={{ column: snapshot.sort.column }}
                          />
                      )
                    : null}
                {snapshot.shownColumns !== undefined
                    ? line(
                          'table.columns',
                          <FormattedMessage id="views.keeps-columns-shown" values={{ count: snapshot.shownColumns }} />
                      )
                    : null}
            </div>
        </div>
    );
}

function CheckboxRow({
    checked,
    onChange,
    children,
}: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    children: ReactNode;
}) {
    return (
        <label className="flex min-h-9 w-fit cursor-pointer items-center gap-2.5 text-sm">
            <Checkbox checked={checked} onCheckedChange={(next) => onChange(next)} />
            {children}
        </label>
    );
}

function DialogBody({
    model,
    dialog,
    snapshot,
}: {
    model: ListViewsModel;
    dialog: ViewDialog;
    snapshot: ListSnapshot | undefined;
}) {
    const { teamName } = APIUserSlice.endpoints.getUserData.useQueryState(undefined, {
        selectFromResult: ({ data }) => ({ teamName: data?.teamName ?? null }),
    });
    const { control, register, setValue, handleSubmit, formState } = useForm<FormValues>({
        defaultValues: initialValues(dialog),
    });
    const [pin, setPin] = useState(false);
    const [openOn, setOpenOn] = useState(false);
    const [visibility, name] = useWatch({ control, name: ['visibility', 'name'] });
    const isCreate = dialog.mode === 'create';
    const hasName = dialog.mode === 'share' || !!name?.trim();

    const visibilityOptions: { value: ListViewVisibility; label: ReactNode }[] = [
        { value: 'PRIVATE', label: t('views.only-me') },
        ...(teamName
            ? [
                  {
                      value: 'TEAM' as const,
                      label: (
                          <>
                              {t('views.my-team')} <span className="text-muted-foreground">· {teamName}</span>
                          </>
                      ),
                  },
              ]
            : []),
        { value: 'PEOPLE', label: t('views.specific-people') },
    ];

    const submit = handleSubmit((values) =>
        // A refusal is already shown by the API layer; the dialog stays open on it.
        model.submitDialog({ ...values, name: values.name.trim(), pin, openOn }).catch(() => undefined)
    );

    return (
        <form className="flex min-h-0 flex-col" onSubmit={submit}>
            <div className="border-neutral-tint flex h-[60px] shrink-0 items-center border-b ps-6 pe-3">
                <DialogTitle className="text-dialog-title not-new:tracking-normal flex-1 font-semibold">
                    <FormattedMessage
                        id={
                            dialog.mode === 'create'
                                ? dialog.titleId
                                : dialog.mode === 'share'
                                  ? 'views.share'
                                  : 'views.rename'
                        }
                    />
                </DialogTitle>
                <DialogClose render={<Button variant="ghost" size="icon" className="text-foreground-tertiary" />}>
                    <XIcon className="size-4" />
                    <span className="sr-only">
                        <FormattedMessage id="close" />
                    </span>
                </DialogClose>
            </div>
            <div className="flex min-h-0 flex-col gap-[18px] overflow-y-auto px-6 py-5">
                {dialog.mode !== 'share' ? (
                    <label className="flex flex-col gap-1.5">
                        <FieldLabel>
                            <FormattedMessage id="views.name" />
                        </FieldLabel>
                        <Input
                            autoFocus
                            maxLength={80}
                            className="bg-paper h-10 px-2.5"
                            {...register('name', { validate: (value) => !!value.trim() })}
                        />
                    </label>
                ) : null}
                {dialog.mode === 'create' && dialog.describesList && snapshot ? (
                    <KeepsBox snapshot={snapshot} />
                ) : isCreate ? (
                    <p className="text-muted-foreground text-sm">
                        <FormattedMessage id="views.keeps" />
                    </p>
                ) : null}
                {dialog.mode !== 'rename' ? (
                    <div className="flex flex-col gap-1.5">
                        <FieldLabel>
                            <FormattedMessage id="views.who-can-see" />
                        </FieldLabel>
                        <RadioGroup
                            value={visibility}
                            onValueChange={(value) => setValue('visibility', value as ListViewVisibility)}
                            aria-label={t('views.who-can-see')}
                            className="gap-1.5"
                        >
                            {visibilityOptions.map((option) => {
                                const isChecked = visibility === option.value;
                                return (
                                    <div
                                        key={option.value}
                                        className={cn(
                                            'border-border-menu flex flex-col rounded-md border-[1.5px]',
                                            isChecked && 'border-primary bg-primary-wash'
                                        )}
                                    >
                                        <label className="flex min-h-10.5 cursor-pointer items-center gap-2.5 px-3 py-2 text-sm">
                                            <RadioGroupItem value={option.value} />
                                            <span className="flex-1">{option.label}</span>
                                        </label>
                                        {option.value === 'PEOPLE' && isChecked ? (
                                            <DynamicMultiSelectInput<FormValues, EmployeeListItemMinDto>
                                                control={control}
                                                setValue={setValue}
                                                name="shared"
                                                wrapperClassName="px-3 pb-2"
                                                placeholder="views.add-colleague"
                                                paginated={{
                                                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                                                    searchField: 'fullName',
                                                }}
                                                getOptionLabel="name"
                                                getOptionIdentifier="id"
                                            />
                                        ) : null}
                                    </div>
                                );
                            })}
                        </RadioGroup>
                        <p className="text-muted-foreground text-meta leading-[1.45]">
                            <FormattedMessage id="views.owner-only" />
                        </p>
                    </div>
                ) : null}
                {isCreate ? (
                    <div className="flex flex-col gap-1">
                        <CheckboxRow checked={pin} onChange={setPin}>
                            <FormattedMessage id="views.pin" />
                        </CheckboxRow>
                        <CheckboxRow checked={openOn} onChange={setOpenOn}>
                            <FormattedMessage id="views.open-on" />
                        </CheckboxRow>
                    </div>
                ) : null}
            </div>
            <div className="border-neutral-tint flex h-[68px] shrink-0 items-center justify-end gap-2.5 border-t px-6">
                <Button
                    variant="outline"
                    type="button"
                    className="border-border-control bg-paper text-foreground h-10 px-4 font-medium shadow-none"
                    onClick={model.closeDialog}
                >
                    <FormattedMessage id="cancel" />
                </Button>
                <Button
                    variant="secondary"
                    type="submit"
                    className="h-10 px-4 font-semibold"
                    disabled={formState.isSubmitting || !hasName}
                >
                    <FormattedMessage id={isCreate ? 'views.save-view' : 'save'} />
                </Button>
            </div>
        </form>
    );
}

/** Saves a new view, or changes who sees a view or its name. */
export default function SaveViewDialog({ model, snapshot }: { model: ListViewsModel; snapshot?: ListSnapshot }) {
    const { dialog } = model;
    return (
        <Dialog open={!!dialog} onOpenChange={(open) => !open && model.closeDialog()}>
            <DialogContent
                showCloseButton={false}
                className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[520px]"
            >
                {/* Keyed so each opening starts from its own values. */}
                {dialog ? (
                    <DialogBody key={JSON.stringify(dialog)} model={model} dialog={dialog} snapshot={snapshot} />
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
